import { Injectable } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';

import { DataMap } from '@domain/interface/data-map';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';
import { LOAD_DATA_MAP } from './data-maps/load';
import { LANE_DATA_MAP } from './data-maps/lane';
import { PROJECT_DATA_MAP } from './data-maps/project';
import { PRICE_LANE_DATA_MAP } from './data-maps/price-lane';
import { BROKER_DATA_MAP } from './data-maps/broker';
import { CustomRestService } from '@service/customRest/custom-rest.service';
import { UtilService } from '@service/util/util.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { RestService } from 'cargo-chief-commons';
import { UrlService } from '@service/url/url.service';
import { IS_VALID_LINE, MAP_HEADERS, MAP_INDICES } from './shared/utils';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { SegmentWrapperService } from '@service/segmentWrapper/segment-wrapper.service';
import { SegmentEventsConstants } from '@domain/constants/segment-events-constants';
import { environment } from '@environment/environment';
import { RFPService } from '@service/rfp/rfp.service';

@Injectable({
	providedIn: 'root',
})
export class CsvUtilService {
	public dataMap: DataMap = {};

	constructor(
		private _formBuilder: UntypedFormBuilder,
		public accountSettings: AccountSettingsService,
		private customRestService: CustomRestService,
		private utilService: UtilService,
		private restService: RestService,
		private urlService: UrlService,
		private cargochief: Cargochief,
		private rfpService: RFPService
	) {
		this.dataMap = {
			load: LOAD_DATA_MAP(this._formBuilder),
			lane: LANE_DATA_MAP(),
			project: PROJECT_DATA_MAP(this._formBuilder),
			priceLane: PRICE_LANE_DATA_MAP(this._formBuilder),
			broker: BROKER_DATA_MAP(this._formBuilder),
		};
	}

	formatFileData(allLines: string[], uploadFileType: string) {
		let csvData = '';
		let indicesMap = MAP_INDICES(allLines[0].split(','));

		for (var i = 0; i < allLines.length; i++) {
			let line = allLines[i];
			if (i == 0) {
				MAP_HEADERS(line.split(','));
			}
			if (IS_VALID_LINE(line, indicesMap, this.dataMap[uploadFileType].requiredHeaders)) {
				let data = line.split(',');
				let row = '';
				let lastIndex = data.length - 1;
				for (var j = 0; j < data.length; j++) {
					let cellContent = data[j];
					row += cellContent.trim() + (j !== lastIndex ? ',' : '\n');
				}
				csvData += row;
			}
		}
		return csvData;
	}

	generateFileFromCSVData(csvFileData, fileName) {
		var blob = new Blob([csvFileData], { type: 'text/csv;charset=utf-8;' });
		return new File([blob], fileName);
	}

	uploadCSVFileToS3PreSignedUrl(
		signedUrl: string,
		objectId: string,
		fileToUpload: any,
		uploadFileType: string
	) {
		const isRFPAndUsingNewFlow = uploadFileType === 'rfps' && environment.USE_NEW_RFP_FLOW;
		this.customRestService.uploadCsvFileToS3(signedUrl, fileToUpload).subscribe(
			(_) => {
				if (isRFPAndUsingNewFlow) {
					this.utilService.showSnackbar(
						'Successfully uploaded file',
						SNACKBAR.SUCCESS,
						SNACKBAR.ACTION
					);
					this.rfpService.sendRFPSubmittedSegmentEvent(objectId);
					return;
				}
				this.uploadedLoadsFile(objectId, uploadFileType);
			},
			(error) => {
				if (error.status == 200) {
					if (isRFPAndUsingNewFlow) {
						this.utilService.showSnackbar(
							'Successfully uploaded file',
							SNACKBAR.SUCCESS,
							SNACKBAR.ACTION
						);
						this.rfpService.sendRFPSubmittedSegmentEvent(objectId);
						return;
					}
					this.uploadedLoadsFile(objectId, uploadFileType);
				} else {
					this.utilService.showSnackbar(
						error.data || 'Unable to upload now.',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
				}
			}
		);
	}

	uploadedLoadsFile(s3Key: string, uploadFileType: string) {
		let url = null;
		if (uploadFileType === 'rfps') {
			url = this.urlService.bulkPriceLanes(s3Key);
		} else if (uploadFileType === 'loads') {
			url = this.urlService.bulkMatchLoads(s3Key);
		}
		this.restService.POST(url, {}).subscribe(
			(result) => {
				this.utilService.showSnackbar(result.message, SNACKBAR.SUCCESS, SNACKBAR.ACTION);
				if (uploadFileType === 'rfps') {
					this.rfpService.sendRFPSubmittedSegmentEvent(s3Key);
				}
			},
			(error) => {
				this.utilService.showSnackbar(
					error.data || 'Unable to upload now.',
					SNACKBAR.ERROR,
					SNACKBAR.ACTION
				);
			}
		);
	}

	appendUserDataToFileName(fileName: string) {
		const accountId = this.cargochief.ACCOUNT_ID;
		const userId = this.cargochief.USER_ID;
		return `${fileName}_A${accountId}_U${userId}`;
	}
}
